import { IndividualContactId, SuretyAccountId } from "@inrev/common";
import {
	useAdminDeleteIndividualContact,
	useAdminFetchAccountIndividuals,
	useAdminFetchAccountLatestIndemnityAgreement,
} from "@portal/api/account/admin";
import { IndividualContactCard } from "@portal/components/features/contact-cards";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { DateTime } from "luxon";
import { FC, useState } from "react";
import { formatName } from "../../../../../utils";
import { AdminIndividualModal } from "../../../../admin/account/shared/AccountIndividualModal.tsx";
import { SectionHeaderWithAdd } from "./SectionHeaderWithAdd.tsx";

type AdminAccountIndividualsSectionProps = {
	accountId: SuretyAccountId;
};

export const AdminAccountIndividualsSection: FC<AdminAccountIndividualsSectionProps> = ({
	accountId,
}) => {
	const { individuals, individualsError, individualsIsFetching, refetchIndividuals } =
		useAdminFetchAccountIndividuals(accountId);
	const {
		latestIndemnityAgreement,
		latestIndemnityAgreementError,
		latestIndemnityAgreementIsFetching,
		refetchLatestIndemnityAgreement,
	} = useAdminFetchAccountLatestIndemnityAgreement(accountId);
	const { deleteIndividualContact } = useAdminDeleteIndividualContact(accountId);
	const [selectedIndividualId, setSelectedIndividualId] = useState<
		IndividualContactId | "new" | undefined
	>(undefined);

	const indemnitorIndividualIds = new Set(
		latestIndemnityAgreement?.indemnitors.individuals.map((indemnitor) => indemnitor.id) ?? [],
	);

	const error = individualsError ?? latestIndemnityAgreementError;

	return (
		<div
			data-testid="admin-account-individuals-section"
			className="w-full flex flex-col space-y-[20px]"
		>
			{error ? (
				<ReportedRetryableLoadError
					error={error}
					resource="account individuals"
					retrying={individualsIsFetching || latestIndemnityAgreementIsFetching}
					onRetry={() => {
						refetchIndividuals();
						refetchLatestIndemnityAgreement();
					}}
				/>
			) : !individuals || latestIndemnityAgreement === undefined ? (
				<CenteredSpinner />
			) : (
				<>
					<SectionHeaderWithAdd
						title="Individuals"
						testId="add-individual"
						onAdd={() => setSelectedIndividualId("new")}
					/>
					{selectedIndividualId && (
						<AdminIndividualModal
							accountId={accountId}
							individuals={individuals}
							selectedIndividualId={
								selectedIndividualId === "new" ? undefined : selectedIndividualId
							}
							onClose={() => setSelectedIndividualId(undefined)}
						/>
					)}
					<div className="w-full flex gap-[15px] flex-wrap">
						{!individuals.length && "None"}
						{individuals.map((individual) => {
							const { ofacCheck, config, spouse } = individual;
							const isIndemnitor = indemnitorIndividualIds.has(individual.id);
							const spouseName = spouse ? formatName(spouse) : undefined;
							const spouseRecord = spouse
								? individuals.find((ind) => ind.id === spouse.id)
								: undefined;
							const spouseIsOwner = spouseRecord?.type === "owner";
							const ofacStale =
								!!config?.contract.includeInUnderwriting &&
								ofacCheck &&
								DateTime.fromISO(ofacCheck.createdAt).diffNow("years").years < -1;
							return (
								<IndividualContactCard
									key={individual.id}
									individual={individual}
									spouseName={spouseName}
									statusCells={[
										{ label: "Indemnifies", active: !!config?.contract.includeInIndemnity },
										{ label: "Underwriting", active: !!config?.contract.includeInUnderwriting },
										{
											label: "OFAC",
											active: ofacCheck?.hit,
											activeColor: "danger",
											partial: !!ofacStale,
											tooltip: ofacCheck
												? `Last pulled ${DateTime.fromISO(ofacCheck.createdAt).toLocaleString(DateTime.DATE_SHORT)}`
												: undefined,
										},
									]}
									onEdit={() => setSelectedIndividualId(individual.id)}
									onDelete={isIndemnitor ? undefined : () => deleteIndividualContact(individual.id)}
									deleteSubMessage={
										individual.type === "owner" && spouseName && !spouseIsOwner
											? `This will also delete their spouse, ${spouseName}.`
											: undefined
									}
								/>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
};
